import React, { useCallback, useContext, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import Alert from 'choerodon-ui/lib/alert';
import { getConfig } from 'choerodon-ui/lib/configure';
import Popover from 'choerodon-ui/lib/popover';
import { action } from 'mobx';
import Radio from '../radio';
import Typography from '../typography';
import isOverflow from '../overflow-tip/util';
import { hide, show } from '../tooltip/singleton';
import { exportExcel } from '../data-set/utils';
import TableContext from './TableContext';
import { $l } from '../locale-context';
import Tooltip from '../tooltip';
import Button from '../button';
import { ButtonColor, FuncType } from '../button/enum';
import { Size } from '../core/enum';
import message from '../message';
const { Text } = Typography;
const ClipboardBar = function ClipboardBar(props) {
    const { clipboard } = props;
    const { tableStore, prefixCls, dataSet } = useContext(TableContext);
    const refText = useRef(null);
    const handleDownloadtemplate = useCallback(() => {
        const getXlsxConfig = getConfig('xlsx');
        if (getXlsxConfig.name !== 'xlsx') {
            message.warning($l('Table', 'no_xlsx'));
            return;
        }
        const { columnGroups: { leafs } } = tableStore;
        const templateHeader = {}; // 表头
        const templateType = {}; // 字段类型
        const templateIsMutiple = {}; // 是否多值
        for (let i = 0; i < leafs.length; i++) {
            const item = leafs[i];
            const columnName = item && item.column.name;
            if (columnName) {
                const field = dataSet.fields.get(columnName);
                if (field) {
                    const label = field.get('label');
                    const type = field.get('type');
                    const isMutiple = field.get('multiple');
                    templateHeader[columnName] = label;
                    templateType[columnName] = type;
                    templateIsMutiple[columnName] = isMutiple;
                }
            }
        }
        const data = [];
        // 模拟 5 条数据
        for (let i = 0; i < 5; i++) {
            const row = {};
            Object.keys(templateHeader).forEach(key => {
                switch (templateType[key]) {
                    case "string":
                    case "intl":
                        row[key] = templateIsMutiple[key] ? '文字1,文字2' : '文字';
                        break;
                    case "number":
                        row[key] = templateIsMutiple[key] ? "10, 20" : Math.floor(Math.random() * 100 + 1);
                        break;
                    case "boolean":
                        row[key] = Math.random() > 0.5 ? "是" : "否";
                        break;
                    case "object":
                        row[key] = templateIsMutiple[key] ? "LOV相关文字1,LOV相关文字2" : "LOV相关文字";
                        break;
                    case "date":
                        row[key] = templateIsMutiple[key] ? `2023-07-01,2023-07-02` : `2023-07-01`;
                        break;
                    case "dateTime":
                        row[key] = templateIsMutiple[key] ? `2023-07-01 12:12:12,2023-07-02 13:13:13` : `2023-07-01 12:12:12`;
                        break;
                    case "year":
                        row[key] = templateIsMutiple[key] ? '2023,2024' : '2023';
                        break;
                    case "week":
                        row[key] = templateIsMutiple[key] ? '2023-20周,2023-21周' : '2023-20周';
                        break;
                    case "month":
                        row[key] = templateIsMutiple[key] ? '2023-01,2024-01' : '2023-01';
                        break;
                    case "time":
                        row[key] = templateIsMutiple[key] ? '05:05:00,16:14:15' : '16:14:15';
                        break;
                    case "email":
                        row[key] = templateIsMutiple[key] ? 'xxxxxx@xxx.com,zzzzz@zzz.com' : 'xxxxxx@xxx.com';
                        break;
                    case "url":
                        row[key] = templateIsMutiple[key] ? 'https:www.xxxxxx.com/,https:www.zzzzzz.com/' : 'https:www.xxxxxx.com/';
                        break;
                    case "bigNumber":
                        row[key] = templateIsMutiple[key] ? '123456789.123456789,1000000.000000001' : '123456789.123456789';
                        break;
                    default:
                        break;
                }
            });
            data.push(row);
        }
        data.unshift(templateHeader);
        exportExcel(data, $l('Table', 'paste_template'), getXlsxConfig);
    }, [dataSet]);
    const clipboardDescription = useMemo(() => {
        if (clipboard.description) {
            return clipboard.description;
        }
        const isMac = navigator.platform.toLowerCase().includes('mac');
        const info = [React.createElement("span", { key: "info-header" }, $l('Table', 'table_support'))];
        if (clipboard.copy) {
            info.push(React.createElement("span", { key: "info-copy" },
                " ",
                React.createElement(Text, { keyboard: true },
                    isMac ? 'command' : 'CTRL',
                    "+C"),
                " ",
                $l('Table', 'ctrl_c_info')));
        }
        if (clipboard.paste) {
            info.push(React.createElement("span", { key: "info-paste" },
                " ",
                React.createElement(Text, { keyboard: true },
                    isMac ? 'command' : 'CTRL',
                    "+V"),
                " ",
                $l('Table', 'ctrl_v_info')));
        }
        return info;
    }, [clipboard]);
    const handleChange = useCallback(action((value) => {
        tableStore.isCopyPristine = value === 'pristine';
        // 切换之后，Table 聚焦
        tableStore.node.focus();
    }), []);
    const suffixInfo = useMemo(() => {
        const btn = [];
        if (clipboard.copy) {
            btn.push(React.createElement(Popover, { key: "clipboard-copy", trigger: "hover", content: React.createElement("form", null,
                    React.createElement(Radio, { name: "base", value: "display", defaultChecked: true, onChange: handleChange }, $l('Table', 'copy_display')),
                    React.createElement(Radio, { name: "base", value: "pristine", onChange: handleChange }, $l('Table', 'copy_pristine'))) },
                React.createElement("a", null, $l('Table', 'copy_config'))));
        }
        if (clipboard.paste) {
            btn.push([
                React.createElement("a", { key: "clipboard-paste", onClick: handleDownloadtemplate, style: { marginLeft: clipboard.copy ? '0.16rem' : 0 } }, $l('Table', 'download_table_template')),
                React.createElement(Tooltip, { title: $l('Table', 'download_table_template_tooltip'), theme: "light", key: "download_table_template" },
                    React.createElement(Button, { funcType: FuncType.flat, color: ButtonColor.primary, size: Size.small, icon: 'help_outline' })),
            ]);
        }
        return btn;
    }, [clipboard]);
    const handleMouseEnter = useCallback(() => {
        if (refText.current && isOverflow(refText.current)) {
            show(refText.current, {
                title: clipboardDescription,
                theme: 'dark',
            });
        }
    }, [clipboard, refText]);
    const handleMouseLeave = useCallback(() => {
        if (refText.current) {
            hide();
        }
    }, [refText]);
    return (React.createElement("div", { className: `${prefixCls}-clipboard` },
        React.createElement(Alert, { message: React.createElement("div", { className: `${prefixCls}-clipboard-content` },
                React.createElement("div", { ref: refText, className: `${prefixCls}-clipboard-content-text`, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave }, clipboardDescription),
                React.createElement("div", { className: `${prefixCls}-clipboard-content-suffix` }, suffixInfo)), type: "info", showIcon: true })));
};
ClipboardBar.displayName = 'ClipboardBar';
export default observer(ClipboardBar);
